import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isAddressEqual } from 'viem';
import { useAccount } from 'wagmi';
import { SchemaSignerDto } from '../../generated/apiTypes';
import { useActiveSigner, useLinkedSigners } from '../../hooks/useActiveSigner';
import { formatDate, formatExpirationDuration } from '../../lib/formatters';
import { Alert } from '../Alert';
import { Button } from '../catalyst-ui-kit/button/button';
import { HexValue } from '../HexValue/HexValue';
import { RelativeDate } from '../RelativeDate/RelativeDate';
import { StateTable } from '../Table/Table';

interface TradingSessionsTableUiProps {
  linkedSigners: SchemaSignerDto[];
  isPending: boolean;
  isFetching: boolean;
  isConnected: boolean;
  onRevoke: (signerAddress: `0x${string}`) => void;
  disableRevoke: boolean;
}

export const TradingSessionsTableUi: React.FC<TradingSessionsTableUiProps> = ({
  linkedSigners,
  isPending,
  isFetching,
  isConnected,
  onRevoke,
  disableRevoke,
}) => {
  const { data: activeSigner } = useActiveSigner();
  const columnHelper = createColumnHelper<SchemaSignerDto>();

  const columns = [
    columnHelper.accessor('createdAt', {
      header: 'Created on',
      cell: (info) => {
        const value = info.getValue();
        const isActive =
          activeSigner && isAddressEqual(info.row.original.signer, activeSigner.signer);
        return (
          <div className="flex items-center text-xs">
            <div className="flex flex-col">
              <span className="font-medium text-contentPrimary">{formatDate(value)}</span>
              <div className="text-3xs text-contentSecondary">
                <RelativeDate timestamp={value} formatter={formatExpirationDuration} />
              </div>
            </div>
            {isActive && (
              <span className="ml-2 rounded bg-neutral-800 px-2 py-0.5 font-medium text-neutral-400">
                Current Session
              </span>
            )}
          </div>
        );
      },
      meta: { align: 'left' },
    }),
    columnHelper.accessor('signer', {
      header: 'Public Address',
      cell: (info) => <HexValue className="text-xs" value={info.getValue()} />,
      enableSorting: false,
    }),
    columnHelper.accessor('expiresAt', {
      header: 'Expires',
      cell: (info) => {
        const expiresAt = info.getValue();
        return (
          <div className="flex flex-col items-start py-1">
            <span className="text-xs">{formatDate(expiresAt)}</span>
            <div className="text-3xs text-contentSecondary">
              <RelativeDate timestamp={expiresAt} formatter={formatExpirationDuration} />
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      cell: (info) => {
        const signerAddress = info.row.original.signer;

        return (
          <Button
            text
            size="2xs"
            color="red"
            disabled={disableRevoke}
            onClick={() => onRevoke(signerAddress)}
          >
            Revoke
          </Button>
        );
      },
      meta: { align: 'right' },
    }),
  ];

  const table = useReactTable({
    data: linkedSigners || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <StateTable
      table={table}
      mobileLayout="table"
      isPending={isPending}
      isFetching={isFetching}
      isConnected={isConnected}
      noDataText="No active trading sessions found."
      tableHeaderClassname="bg-backgroundPrimary"
    />
  );
};

interface TradingSessionsTableProps {
  onRevoke: (signerAddress: `0x${string}`) => void;

  disableRevoke: boolean;
}

export const TradingSessionsTable: React.FC<TradingSessionsTableProps> = ({
  onRevoke,
  disableRevoke,
}) => {
  const { isConnected } = useAccount();
  const {
    data: linkedSigners,
    isPending,
    isFetching,
    error: linkedSignerError,
  } = useLinkedSigners();

  if (linkedSignerError) {
    return <Alert variant="error">Failed to load trading sessions.</Alert>;
  }

  return (
    <TradingSessionsTableUi
      linkedSigners={linkedSigners || []}
      isPending={isPending}
      isFetching={isFetching}
      isConnected={isConnected}
      onRevoke={onRevoke}
      disableRevoke={disableRevoke}
    />
  );
};
